import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Card, CardContent } from "@/components/ui/card";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { useState } from "react";
import { toast } from "sonner";
import { supabase } from "@/integrations/supabase/client";
import {
  MessageSquare, ArrowLeft, Mail, Building2, Users, Send,
} from "lucide-react";

import type { Easing } from "framer-motion";

const ease: Easing = [0.25, 0.1, 0.25, 1];
const fadeUp = {
  hidden: { opacity: 0, y: 24 },
  visible: (i: number) => ({ opacity: 1, y: 0, transition: { delay: i * 0.1, duration: 0.5, ease } }),
};

export default function Contact() {
  const navigate = useNavigate();
  const [sending, setSending] = useState(false);
  const [subject, setSubject] = useState("vendas");

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setSending(true);
    const fd = new FormData(e.currentTarget);
    const name = (fd.get("name") as string || "").trim();
    const email = (fd.get("email") as string || "").trim();
    const company = (fd.get("company") as string || "").trim();
    const phone = (fd.get("phone") as string || "").trim();
    const message = (fd.get("message") as string || "").trim();

    if (!name || !email || !message) {
      toast.error("Preencha os campos obrigatórios.");
      setSending(false);
      return;
    }

    const { error } = await supabase.from("contact_submissions").insert({
      name,
      email,
      company: company || null,
      phone: phone || null,
      message: `[${subject}] ${message}`,
      source: "contact_page",
    });

    setSending(false);
    if (error) {
      toast.error("Erro ao enviar. Tente novamente.");
      return;
    }
    toast.success("Mensagem enviada! Entraremos em contato em até 24h.");
    (e.target as HTMLFormElement).reset();
  };

  return (
    <div className="min-h-screen bg-background text-foreground">
      <nav className="fixed top-0 inset-x-0 z-50 glass-card border-b border-border/50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 flex items-center justify-between h-16">
          <div className="flex items-center gap-2 cursor-pointer" onClick={() => navigate("/")}>
            <MessageSquare className="w-7 h-7 text-primary" />
            <span className="text-xl font-bold font-headline tracking-tight text-primary">SYRA</span>
          </div>
          <Button variant="ghost" size="sm" onClick={() => navigate("/")}>
            <ArrowLeft className="mr-1 h-4 w-4" /> Voltar
          </Button>
        </div>
      </nav>

      <div className="pt-28 pb-20 px-4">
        <div className="max-w-4xl mx-auto">
          <motion.div className="text-center mb-12" initial="hidden" animate="visible" variants={fadeUp} custom={0}>
            <h1 className="text-4xl sm:text-5xl font-extrabold font-headline mb-4">Fale conosco</h1>
            <p className="text-muted-foreground text-lg">Para Enterprise, demos personalizadas ou parcerias.</p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            <motion.div initial="hidden" animate="visible" variants={fadeUp} custom={1}>
              <Card>
                <CardContent className="p-6">
                  <form onSubmit={handleSubmit} className="space-y-4">
                    <div className="grid grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label htmlFor="name">Nome *</Label>
                        <Input id="name" name="name" required placeholder="Seu nome" />
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="company">Empresa</Label>
                        <Input id="company" name="company" placeholder="Sua empresa" />
                      </div>
                    </div>
                    <div className="grid grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label htmlFor="email">E-mail *</Label>
                        <Input id="email" name="email" type="email" required placeholder="seu@email.com" />
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="phone">Telefone</Label>
                        <Input id="phone" name="phone" placeholder="+55 11 99999-9999" />
                      </div>
                    </div>
                    <div className="space-y-2">
                      <Label>Assunto</Label>
                      <Select value={subject} onValueChange={setSubject}>
                        <SelectTrigger><SelectValue /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="vendas">Vendas</SelectItem>
                          <SelectItem value="suporte">Suporte</SelectItem>
                          <SelectItem value="parceria">Parceria</SelectItem>
                          <SelectItem value="outro">Outro</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="message">Mensagem *</Label>
                      <Textarea id="message" name="message" required rows={4} placeholder="Conte-nos sobre sua necessidade..." />
                    </div>
                    <Button type="submit" className="w-full gradient-primary text-primary-foreground" disabled={sending}>
                      {sending ? "Enviando..." : "Enviar mensagem"} <Send className="ml-2 h-4 w-4" />
                    </Button>
                  </form>
                </CardContent>
              </Card>
            </motion.div>

            <motion.div className="space-y-6" initial="hidden" animate="visible" variants={fadeUp} custom={2}>
              <Card>
                <CardContent className="p-6 flex items-start gap-4">
                  <div className="w-10 h-10 rounded-lg gradient-primary flex items-center justify-center text-primary-foreground shrink-0">
                    <Building2 className="h-5 w-5" />
                  </div>
                  <div>
                    <h3 className="font-semibold font-headline mb-1">Enterprise</h3>
                    <p className="text-sm text-muted-foreground">Grupos ilimitados, SLA dedicado, integrações custom e onboarding assistido pela equipe SYRA.</p>
                  </div>
                </CardContent>
              </Card>
              <Card>
                <CardContent className="p-6 flex items-start gap-4">
                  <div className="w-10 h-10 rounded-lg gradient-primary flex items-center justify-center text-primary-foreground shrink-0">
                    <Users className="h-5 w-5" />
                  </div>
                  <div>
                    <h3 className="font-semibold font-headline mb-1">Demo personalizada</h3>
                    <p className="text-sm text-muted-foreground">Agende uma demonstração ao vivo para ver a SYRA em ação com seus casos de uso específicos.</p>
                  </div>
                </CardContent>
              </Card>
              <Card>
                <CardContent className="p-6 flex items-start gap-4">
                  <div className="w-10 h-10 rounded-lg gradient-primary flex items-center justify-center text-primary-foreground shrink-0">
                    <Mail className="h-5 w-5" />
                  </div>
                  <div>
                    <h3 className="font-semibold font-headline mb-1">Contato direto</h3>
                    <p className="text-sm text-muted-foreground">contato@syra.app</p>
                  </div>
                </CardContent>
              </Card>
            </motion.div>
          </div>
        </div>
      </div>
    </div>
  );
}
